import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { toast } from 'react-toastify';
import { processTemplate } from './llm-prompts.service';

const LLMPromptPreview = ({ open, onClose, prompt }) => {
  const [variables, setVariables] = useState({});
  const [result, setResult] = useState('');
  const [processing, setProcessing] = useState(false);

  // Extract {variable} placeholders from prompt content
  const templateVars = useMemo(() => {
    if (!prompt?.promptContent) return [];
    const matches = prompt.promptContent.match(/\{(\w+)\}/g) || [];
    return [...new Set(matches.map((m) => m.slice(1, -1)))];
  }, [prompt?.promptContent]);

  const handleVarChange = (varName) => (e) => {
    setVariables((prev) => ({ ...prev, [varName]: e.target.value }));
  };

  const handleProcess = async () => {
    setProcessing(true);
    try {
      const res = await processTemplate(prompt.promptContent, variables);
      setResult(res?.result || '');
      toast.success('Template processed');
    } catch (e) {
      toast.error(e || 'Failed to process template');
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setVariables({});
    setResult('');
    onClose();
  };

  if (!prompt) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Test Template
        <Typography variant="body2" color="text.secondary" component="div">
          <code>{prompt.promptKey}</code> (v{prompt.version})
        </Typography>
      </DialogTitle>
      <DialogContent>
        {templateVars.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No template variables detected in this prompt. Template variables use the <code>{'{variable}'}</code> syntax.
          </Alert>
        ) : (
          <>
            <Typography variant="subtitle2" mb={1.5}>
              Template Variables ({templateVars.length})
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.5} mb={2}>
              {templateVars.map((v) => (
                <TextField
                  key={v}
                  label={v}
                  value={variables[v] || ''}
                  onChange={handleVarChange(v)}
                  size="small"
                  fullWidth
                  inputProps={{ style: { fontFamily: 'monospace' } }}
                  helperText={`Replaces {${v}} in the template`}
                />
              ))}
            </Box>
            <Button
              variant="contained"
              onClick={handleProcess}
              disabled={processing}
              fullWidth
              sx={{ mb: 2 }}
            >
              {processing ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
              Process Template
            </Button>
          </>
        )}

        {/* Original content */}
        <Typography variant="subtitle2" mb={0.5}>Original Content</Typography>
        <Box
          sx={{
            p: 1.5,
            bgcolor: '#f5f5f5',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            maxHeight: 200,
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: 13,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            mb: 2,
          }}
        >
          {prompt.promptContent}
        </Box>

        {/* Processed result */}
        {result && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" mb={0.5} color="success.main">
              Processed Output
            </Typography>
            <Box
              sx={{
                p: 1.5,
                bgcolor: '#f0fdf4',
                border: '1px solid #22c55e',
                borderRadius: 1,
                maxHeight: 300,
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: 13,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {result}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LLMPromptPreview;
